import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import {
  Box,
  Button,
  FormControl,
  TextField,
  MenuItem,
  InputLabel,
  Select,
  SelectChangeEvent,
  CircularProgress
} from '@mui/material';
import { toast } from 'react-toastify';

interface AnnouncementProps {
  cpeId: string;
  onSubmit: () => void;
}

const pad2 = (n: number) => n.toString().padStart(2, '0');

const toUtcFormat = (d: Date) => {
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate() + 1)}T${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}:${pad2(d.getUTCSeconds())}.000000+00:00`;
};

export const RouterAnnouncement = ({ cpeId, onSubmit }: AnnouncementProps) => {
  const api = useApi();

  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createCpeAnnouncement = useMutation(api.cpeAddAnnouncementApiV1CpeAddAnnouncementPost, {
    onSuccess() {
      toast.success('Maintenance announcement created.');
      onSubmit();
      setIsSubmitting(false);
    },
    onError: (error) => {
      apiError(error);
      setIsSubmitting(false);
    }
  });

  const createMetroAnnouncement = useMutation(api.metroAddAnnouncementApiV1MetroAddAnnouncementPost, {
    onSuccess() {
      toast.success('Maintenance announcement created.');
      onSubmit();
      setIsSubmitting(false);
    },
    onError: (error) => {
      apiError(error);
      setIsSubmitting(false);
    }
  });

  const handleSubmit = () => {
    if (!date || !time || !duration) {
      toast.error('Please fill out all fields.');
      return;
    }

    const [hour, minute] = time.split(':').map(Number);
    const startLocal = new Date(date);
    startLocal.setHours(hour, minute, 0, 0);

    const start_time = toUtcFormat(startLocal);

    setIsSubmitting(true);

    if (cpeId.startsWith('cpe')) {
      createCpeAnnouncement.mutate({
        cpe_id: cpeId,
        start_time,
        duration: duration,
      });
    } else {
      createMetroAnnouncement.mutate({
        metro_id: cpeId,
        start_time,
        duration: duration,
      });
    }
  };

  const generateTimeOptions = () =>
    Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`);

  return (
    <Box pt={2}>
      <FormControl fullWidth sx={{ mb: 3 }}>
        <TextField
          label="Date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </FormControl>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel id="time-select-label">Time (24h)</InputLabel>
        <Select
          labelId="time-select-label"
          value={time}
          label="Time (24h)"
          onChange={(e: SelectChangeEvent) => setTime(e.target.value)}
        >
          {generateTimeOptions().map((t) => (
            <MenuItem key={t} value={t}>{t}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <TextField
          label="Duration (hours)"
          type="number"
          value={duration}
          onChange={(e) => setDuration(e.target.value)}
          InputProps={{ inputProps: { min: 1 } }}
        />
      </FormControl>

      <Box display="flex" justifyContent="flex-end">
        {isSubmitting && <CircularProgress sx={{ mr: 2 }} />}
        <Button
          variant="outlined"
          onClick={handleSubmit}
          disabled={isSubmitting || !date || !time || !duration}
        >
          Submit
        </Button>
      </Box>
    </Box>
  );
};
